import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Header, ListBox } from '@heroui/react';
import { CategoryPickerOption } from './CategoryPickerOption';
import {
    UNTICKED,
    type CategorySearchGroup,
    type CategorySearchMatch,
    type CategoryTickState,
} from './categoryPickerMeta';

type CategorySearchOptionsProps = {
    groups: CategorySearchGroup[];
    tickStates: ReadonlyMap<string, CategoryTickState>;
};

/**
 * The category picker's list while something is typed (design board
 * **CategorySearch**): the matching categories, flat, grouped under the path
 * above them ("Grocery › Snacks") so same-named ones can be told apart, with
 * the matched text in bold. Top-level matches come without a header.
 * "Uncategorized" is left out: a search lists categories only.
 */
export const CategorySearchOptions: React.FC<CategorySearchOptionsProps> = ({
    groups,
    tickStates,
}) => {
    const { t } = useTranslation('pages');
    const separator = t(
        'products.components.productTable.filters.categoryPathSeparator',
    );

    const renderMatch = ({ node, match }: CategorySearchMatch) => (
        <CategoryPickerOption
            id={node.id}
            key={node.id}
            label={node.name}
            match={match}
            tick={tickStates.get(node.id) ?? UNTICKED}
        />
    );

    return (
        <>
            {groups.map((group) =>
                group.path.length === 0 ? (
                    group.matches.map(renderMatch)
                ) : (
                    <ListBox.Section key={group.key}>
                        <Header>{group.path.join(separator)}</Header>
                        {group.matches.map(renderMatch)}
                    </ListBox.Section>
                ),
            )}
        </>
    );
};
